/**
 * Colour variants of a style's sprites. The learned variant maps only cover sprites whose pixel
 * structure matched; every colour block of a tileset has the same layout, so any other sprite
 * (including ones picked by hand) is found at the offset its nearest mapped neighbour has to its
 * variant, when a sprite of the same size sits there.
 */
import { gameData } from '../data/gameData.ts';
import { customCanonicalEntry, customVariantEntry, isCustomTex } from '../data/customTiles.ts';
import type { PaletteEntry } from '../model/format.ts';

interface StyleLike {
  data: { variants: { name: string; map: Record<string, number> }[] };
  sprites: PaletteEntry[];
}

type Rect = [number, number, number, number];

/** a mapped sprite's neighbourhood: farther than this (px) from any mapped sprite gets no variant */
const REACH = 48;

const texIndex = new Map<string, { byName: Map<string, Rect>; byRect: Map<string, string>; tiles: Map<string, string> }>();

function texInfo(tex: string) {
  let t = texIndex.get(tex);
  if (t) return t;
  let def;
  let tiles: Record<string, { sprite?: { guid?: string; fileID?: string | number } | null }> = {};
  try {
    const gd = gameData();
    def = gd.sprites.textures[tex];
    tiles = gd.sprites.tiles as typeof tiles;
  } catch {
    return null;
  }
  if (!def) return null;
  const byName = new Map<string, Rect>();
  const byRect = new Map<string, string>();
  const idToName = new Map<string, string>();
  for (const s of def.sprites) {
    byName.set(s.n, s.r as Rect);
    byRect.set((s.r as Rect).join(','), s.n);
    idToName.set(String(s.id), s.n);
  }
  const tileOf = new Map<string, string>();
  for (const [guid, tl] of Object.entries(tiles)) {
    const sp = tl?.sprite;
    if (!sp || sp.guid !== tex) continue;
    const n = idToName.get(String(sp.fileID));
    if (n && !tileOf.has(n)) tileOf.set(n, guid);
  }
  t = { byName, byRect, tiles: tileOf };
  texIndex.set(tex, t);
  return t;
}

const rectOf = (pe: PaletteEntry): Rect | null => texInfo(pe.tex)?.byName.get(pe.sprite) ?? null;

function entryAt(tex: string, r: Rect, like?: PaletteEntry): PaletteEntry | null {
  const t = texInfo(tex);
  const n = t?.byRect.get(r.join(','));
  if (!t || !n) return null;
  if (like && like.sprite === n) return like;
  const tile = t.tiles.get(n);
  return tile ? { tex, sprite: n, tile } : { tex, sprite: n };
}

interface Pair {
  tex: string;
  from: Rect;
  to: Rect;
}

function pairsOf(st: StyleLike, vi: number): Pair[] {
  const map = st.data.variants[vi]?.map ?? {};
  const out: Pair[] = [];
  for (const [a, b] of Object.entries(map)) {
    const pa = st.sprites[+a], pb = st.sprites[b];
    if (!pa || !pb || pa.tex !== pb.tex) continue;
    const ra = rectOf(pa), rb = rectOf(pb);
    if (ra && rb) out.push({ tex: pa.tex, from: ra, to: rb });
  }
  return out;
}

function nearest(pairs: Pair[], tex: string, r: Rect, side: 'from' | 'to'): Pair | null {
  let best: Pair | null = null, bd = REACH + 1e-6;
  const cx = r[0] + r[2] / 2, cy = r[1] + r[3] / 2;
  for (const p of pairs) {
    if (p.tex !== tex) continue;
    const q = p[side];
    const d = Math.max(Math.abs(q[0] + q[2] / 2 - cx), Math.abs(q[1] + q[3] / 2 - cy));
    if (d < bd) [bd, best] = [d, p];
  }
  return best;
}

const tables = new WeakMap<StyleLike, Map<number, (PaletteEntry | null)[]>>();

/**
 * Sprite per style sprite index in colour variant `vi` (variant 0: the sprites themselves); null where
 * the variant has no counterpart (the canonical sprite is used).
 */
export function variantSprites(st: StyleLike, vi: number): (PaletteEntry | null)[] {
  let byV = tables.get(st);
  if (!byV) tables.set(st, (byV = new Map()));
  let t = byV.get(vi);
  if (t) return t;
  const map = st.data.variants[vi]?.map;
  if (!vi || !map) t = st.sprites.slice();
  else {
    const pairs = pairsOf(st, vi);
    const own = new Set(Object.values(map));
    const vname = st.data.variants[vi].name;
    t = st.sprites.map((pe, si) => {
      if (isCustomTex(pe.tex)) return customVariantEntry(pe, vname);
      const m = map[si];
      if (m !== undefined) return st.sprites[m] ?? null;
      // a sprite that is itself another colour's piece stays as it is
      if (own.has(si)) return null;
      const r = rectOf(pe);
      const p = r && nearest(pairs, pe.tex, r, 'from');
      if (!r || !p) return null;
      return entryAt(pe.tex, [r[0] + p.to[0] - p.from[0], r[1] + p.to[1] - p.from[1], r[2], r[3]], pe);
    });
  }
  byV.set(vi, t);
  return t;
}

/**
 * The canonical-colour sprite for a sprite of any colour variant of the style (the sprite itself when
 * it is canonical or no variant has it).
 */
export function canonicalSprite(st: StyleLike, pe: PaletteEntry): PaletteEntry {
  if (isCustomTex(pe.tex)) return customCanonicalEntry(pe);
  const vs = st.data.variants;
  for (let vi = 1; vi < vs.length; vi++) {
    for (const [a, b] of Object.entries(vs[vi].map)) {
      const pb = st.sprites[b];
      if (pb && pb.tex === pe.tex && pb.sprite === pe.sprite) return st.sprites[+a] ?? pe;
    }
  }
  const r = rectOf(pe);
  if (!r) return pe;
  for (let vi = 1; vi < vs.length; vi++) {
    const pairs = pairsOf(st, vi);
    const p = nearest(pairs, pe.tex, r, 'to');
    if (!p) continue;
    // only sprites inside that colour's block: closer to its pieces than to the canonical ones
    const home = nearest(pairs, pe.tex, r, 'from');
    if (home) {
      const dTo = Math.hypot(p.to[0] - r[0], p.to[1] - r[1]), dFrom = Math.hypot(home.from[0] - r[0], home.from[1] - r[1]);
      if (dFrom <= dTo) continue;
    }
    const c = entryAt(pe.tex, [r[0] - p.to[0] + p.from[0], r[1] - p.to[1] + p.from[1], r[2], r[3]]);
    if (c) return c;
  }
  return pe;
}
